import Address from "@mapmycustomers/shared/types/Address";
import { RouteManualStop } from "@mapmycustomers/shared/types/entity/Route";
import { RouteEdgeEntity } from "@mapmycustomers/shared/types/entity/RouteDetail";
import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import User from "@mapmycustomers/shared/types/User";

import { EditorRouteStop } from "../../types/RouteStop";
import { isCompanyStop, isManualStop, isPersonStop } from "../../utils/stopPredicates";
import type { RouteEditorState } from "../index";

interface SnapshotEdge {
  address?: Address;
  entityId?: RouteEdgeEntity["id"];
  entityType?: RouteEdgeEntity["entity"];
  geoPoint?: GeoPoint;
}

interface SnapshotStop {
  allottedTime?: number;
  displayOrder: number;
  key: string;
  manualAddress?: RouteManualStop["manualAddress"];
  manualGeoPoint?: RouteManualStop["manualGeoPoint"];
  manualName?: RouteManualStop["manualName"];
  scheduledTime: null | string;
}

export interface RouteEditorSnapshot {
  allottedTime: RouteEditorState["allottedTime"];
  end: SnapshotEdge;
  name: string;
  ownerId?: User["id"];
  routeType: RouteEditorState["routeType"];
  sharedUserIds: User["id"][];
  start: SnapshotEdge;
  startAt?: number;
  stops: SnapshotStop[];
  travelMode: RouteEditorState["travelMode"];
}

const stopKey = (stop: EditorRouteStop): string => {
  if (isCompanyStop(stop)) {
    return `company:${stop.accountId}`;
  }
  if (isPersonStop(stop)) {
    return `person:${stop.contactId}`;
  }
  return `manual:${stop.id}`;
};

const toSnapshotStop = (stop: EditorRouteStop): SnapshotStop => ({
  allottedTime: stop.allottedTime,
  displayOrder: stop.displayOrder,
  key: stopKey(stop),
  manualAddress: isManualStop(stop) ? stop.manualAddress : undefined,
  manualGeoPoint: isManualStop(stop) ? stop.manualGeoPoint : undefined,
  manualName: isManualStop(stop) ? stop.manualName : undefined,
  scheduledTime: stop.scheduledTime ?? null,
});

const toRouteEditorSnapshot = (state: RouteEditorState): RouteEditorSnapshot => ({
  allottedTime: state.allottedTime,
  end: {
    address: state.endAddress,
    entityId: state.endEntityId,
    entityType: state.endEntityType,
    geoPoint: state.endGeoPoint,
  },
  name: state.name,
  ownerId: state.owner?.id,
  routeType: state.routeType,
  sharedUserIds: [...state.sharedUserIds].sort((left, right) => left - right),
  start: {
    address: state.startAddress,
    entityId: state.startEntityId,
    entityType: state.startEntityType,
    geoPoint: state.startGeoPoint,
  },
  startAt: state.startAt ? state.startAt.getTime() : undefined,
  stops: state.stops.map(toSnapshotStop),
  travelMode: state.travelMode,
});

export default toRouteEditorSnapshot;
